/**
 * buyCreditsModel — Envío CR-1 · cálculos de la página «Cargar créditos».
 * Funciones puras (sin React, sin red): fáciles de testear.
 *
 * Los precios salen SIEMPRE del backend (GET /payments/mercadopago/packages,
 * que lee PACKAGE_<ID>_USD). Acá no hay precios fijos: si un paquete viene sin
 * precio, la tarjeta se muestra como no disponible.
 */

/** Créditos que usa cada tipo de contenido (tabla actual de nodos; aproximado). */
export const OUTPUT_TYPES = [
  { key: 'img', per: 2, unit: 'imágenes', label: 'Imágenes', hint: 'en alta calidad, hasta 4K', color: '#4ea2ff' },
  { key: 'vid', per: 2, unit: 'segundos de video', label: 'Segundos de video', hint: 'con movimiento de cámara y sonido', color: '#9b7bff' },
  { key: 'ava', per: 5, unit: 'escenas de avatar', label: 'Escenas de avatar', hint: 'tu personaje hablando con su voz', color: '#36d39a' },
  { key: 'voz', per: 3, unit: 'locuciones', label: 'Locuciones', hint: 'voces naturales en varios idiomas', color: '#ff5fa8' },
  { key: 'mus', per: 4, unit: 'canciones', label: 'Canciones', hint: 'música original para tus piezas', color: '#ffb547' },
];

/** Color de acento por paquete (se usa en el selector y en los números). */
export const PACK_ACCENT = { starter: '#4ea2ff', pro: '#9b7bff', studio: '#36d39a' };
export const accentOf = (id) => PACK_ACCENT[id] || '#4ea2ff';

export const fmtInt = (n) => Math.floor(Number(n) || 0).toLocaleString('es-AR');

export const formatMoney = (amount, currency, maxDecimals = 2) => {
  if (amount == null || Number.isNaN(Number(amount))) return '—';
  try {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency,
      currencyDisplay: currency === 'USD' ? 'code' : 'symbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: maxDecimals,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
};

/** Cuánto produce una carga si se usa entera en un solo tipo de contenido. */
export function outputsFor(credits) {
  const c = Number(credits) || 0;
  return OUTPUT_TYPES.map((t) => ({ ...t, qty: Math.floor(c / t.per) }));
}

/** Precio USD de un paquete según el backend (null = sin precio). */
export const usdOf = (pkg) =>
  pkg && pkg.amount_usd != null && Number(pkg.amount_usd) > 0 ? Number(pkg.amount_usd) : null;

/** % de ahorro por crédito respecto del paquete más chico con precio (0 si no hay ahorro). */
export function savingsPct(pkg, packages) {
  const list = (packages || []).filter((p) => usdOf(p) != null && Number(p.credits) > 0);
  if (!pkg || usdOf(pkg) == null || !(Number(pkg.credits) > 0) || list.length < 2) return 0;
  const base = list.reduce((a, b) => (Number(a.credits) <= Number(b.credits) ? a : b));
  const baseUnit = usdOf(base) / Number(base.credits);
  const unit = usdOf(pkg) / Number(pkg.credits);
  const pct = Math.round((1 - unit / baseUnit) * 100);
  return pct > 0 ? pct : 0;
}

/** Ordena por créditos y completa lo que muestra cada tarjeta. */
export function decoratePackages(packages) {
  const list = [...(packages || [])].sort((a, b) => Number(a.credits) - Number(b.credits));
  const priced = list.filter((p) => usdOf(p) != null);
  const top = priced.length ? priced[priced.length - 1].id : null;
  return list.map((p) => {
    const usd = usdOf(p);
    const pct = savingsPct(p, list);
    const buyable = p.available !== false && usd != null && p.amount != null;
    let badge = '';
    if (buyable && p.id === 'pro') badge = 'El más elegido';
    else if (buyable && p.id === top && pct > 0) badge = 'Mejor precio por crédito';
    return {
      ...p,
      name: p.label || p.id,
      usd,
      savings: pct,
      savingsText: !buyable ? 'No disponible en este momento' : pct > 0 ? `Ahorrás ${pct} % en cada crédito` : 'Precio de lista',
      badge,
      uses: outputsFor(p.credits).slice(0, 4),
      buyable,
    };
  });
}

/** Textos del bloque de ahorro para el pack elegido en «¿Qué podés crear?». */
export function savingsCopy(pkg) {
  if (!pkg || !pkg.savings) {
    return {
      title: 'Pagás sólo lo que usás',
      text: 'Sin planes ni mínimos. Cargás cuando lo necesitás y tus créditos no vencen.',
      sticker: 'Precio de lista',
      hot: false,
    };
  }
  const sticker = `${pkg.savings} % de ahorro en cada crédito`;
  return pkg.badge === 'Mejor precio por crédito'
    ? { title: 'La carga que más rinde', text: `Con el ${pkg.name}, cada crédito te sale al mejor precio.`, sticker, hot: true }
    : { title: 'Más carga, menos por crédito', text: `Con el ${pkg.name}, cada crédito te sale más barato que en el pack más chico.`, sticker, hot: true };
}
